/**
 * ErrorBoundary - Catches React errors and reports them
 * Also provides fallback UI for crashed components
 */
import React from 'react';
import axios from 'axios';
import { API } from '@/utils/api';

// Generate session ID for error grouping
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('error_session_id');
  if (!sessionId) {
    sessionId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('error_session_id', sessionId);
  }
  return sessionId;
};

// Report error to backend
const reportError = async (error, errorInfo, additionalData = {}) => {
  try {
    const userId = localStorage.getItem('user_id') || null;
    
    await axios.post(`${API}/errors/report`, {
      message: error.message || String(error),
      stack: error.stack || null,
      componentStack: errorInfo?.componentStack || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId,
      sessionId: getSessionId(),
      severity: 'error',
      metadata: {
        ...additionalData,
        timestamp: new Date().toISOString(),
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        language: navigator.language,
        online: navigator.onLine
      }
    });
    
    console.debug('[ErrorBoundary] Error reported to backend');
  } catch (reportError) {
    console.error('[ErrorBoundary] Failed to report error:', reportError);
  }
};

// Global error handler for uncaught errors
export const setupGlobalErrorHandling = () => {
  // Handle uncaught JS errors
  window.onerror = (message, source, lineno, colno, error) => {
    reportError(
      error || { message, stack: `${source}:${lineno}:${colno}` },
      null,
      { type: 'uncaught_error', source, lineno, colno }
    );
    return false; // Don't prevent default handling
  };
  
  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    reportError(
      error instanceof Error ? error : { message: String(error) },
      null,
      { type: 'unhandled_rejection' }
    );
  };
  
  console.debug('[ErrorBoundary] Global error handling initialized');
};

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Report to backend
    reportError(error, errorInfo, {
      componentName: this.props.componentName || 'Unknown'
    }).then(() => {
      // Could set errorId from response if needed
    });
    
    // Also log to console for debugging
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default error UI
      return (
        <div className="min-h-screen bg-zinc-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
              Ups! Coś poszło nie tak
            </h1>
            
            <p className="text-zinc-500 dark:text-zinc-400 mb-6">
              Wystąpił nieoczekiwany błąd. Nasz zespół został powiadomiony 
              i pracujemy nad rozwiązaniem.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-mono text-red-600 dark:text-red-400 break-all">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="text-xs text-red-500 mt-2 overflow-auto max-h-32">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                Spróbuj ponownie
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 px-4 py-2 bg-zinc-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-zinc-700 dark:text-white rounded-lg font-medium transition-colors"
              >
                Strona główna
              </button>
            </div>
            
            <p className="mt-6 text-xs text-zinc-400">
              Jeśli problem się powtarza, skontaktuj się z nami
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
